import { Task } from '../types';
import apiService from '../../../../services/api';

export const isOverdue = (dueDate: string): boolean => {
  if (!dueDate) return false;
  return new Date(dueDate) < new Date();
};

export const formatDate = (dateString: string): string => {
  if (!dateString) return 'N/A';
  try {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  } catch {
    return 'N/A';
  }
};

export const calculateStats = (
  tasks: Task[],
  activePersonalTab?: 'assigned' | 'approvals' | 'initiatives' | 'requests',
  isWorkMode: boolean = false,
  activeWorkTab?: 'tasks' | 'templates'
) => {
  const totalTasks = tasks.length;

  // Template Mode Stats: Only Total Count matters
  if (isWorkMode && activeWorkTab === 'templates') {
    return {
      totalTasks,
      inProgress: 0,
      completed: 0,
      pointsEarned: 0
    };
  }

  if (!isWorkMode && (activePersonalTab === 'approvals' || activePersonalTab === 'requests')) {
    const pendingReview = tasks.filter((t: Task) =>
      t.status === 'pending' || t.status === 'in_progress'
    ).length;
    const approved = tasks.filter((t: Task) => t.status === 'completed').length;
    const rejectedOrOthers = tasks.filter((t: Task) =>
      t.status === 'cancelled' || t.status === 'on_hold'
    ).length;

    return {
      totalTasks,
      inProgress: pendingReview,
      completed: approved,
      pointsEarned: rejectedOrOthers
    };
  }
  const inProgress = tasks.filter((t: Task) => t.status === 'in_progress').length;
  const completed = tasks.filter((t: Task) => t.status === 'completed').length;

  let pointsEarned = 0;
  if (isWorkMode) {
    pointsEarned = tasks.reduce((sum: number, task: Task) => {
      const taskPoints = task.assignees?.reduce((assigneeSum: number, assignee: any) => {
        return assigneeSum + (assignee.points_earned || 0);
      }, 0) || 0;
      return sum + taskPoints;
    }, 0);
  } else {
    if (activePersonalTab === 'assigned') {
      pointsEarned = tasks.reduce((sum: number, task: Task) => {
        const userPoints = task.assignees?.find((a: any) => a.user?.id)?.points_earned || 0;
        return sum + userPoints;
      }, 0);

    } else {
      pointsEarned = tasks.reduce((sum: number, task: Task) => sum + (task.points_earned || 0), 0);
    }
  }

  return {
    totalTasks,
    inProgress,
    completed,
    pointsEarned
  };
};

// Function to load proposal requests (as requested)


export const fetchProposalRequests = async (filters: any) => {
  try {
    const response = await apiService.getPendingProposalApprovals(filters);
    const proposals = (response as any).results || (Array.isArray(response) ? response : []) || [];

    // Map TaskProposal to Task interface for compatibility with TaskCard
    return proposals.map((p: any) => ({
      id: p.id,
      name: p.title,
      description: p.description,
      status: p.status, // pending_approval
      priority: p.priority || 'medium',
      category: p.category || { id: '', name: 'Uncategorized', color: '#9ca3af', icon: 'folder' },
      points: 0, // Proposals don't have points yet?
      assigned_to: [], // Approver context
      created_by: {
        id: p.requester_details?.id,
        first_name: p.requester_details?.full_name?.split(' ')[0] || '',
        last_name: p.requester_details?.full_name?.split(' ').slice(1).join(' ') || '',
        full_name: p.requester_details?.full_name || 'Unknown',
        email: p.requester_details?.email || '',
        role: 'member', // Assumed
        is_active: true,
        created_at: '',
        updated_at: ''
      },
      due_date: p.due_date,
      created_at: p.created_at,
      updated_at: p.updated_at,
      group: p.group || { id: '', name: '' },
      requires_approval: true,
      requires_documents: false,
      is_instance: true, // Treat as instance for rendering?
      is_template: false,
      is_proposal: true, // Marker for UI differentiation if needed
      current_stage: p.current_stage
    }));
  } catch (error: any) {
    console.error('Error fetching proposal requests:', error);
    return [];
  }
};

